<script setup lang="ts">
import {computed, onUnmounted, watch} from "vue";
import {storeToRefs} from "pinia";
import {useNotificationsStore} from "@/stores/ToastDataStore";
import {useLoginInfoStore} from "@/stores/loginInfo";
import Translator from "@/utils/common/Translator.ts";
import NotificationUtils from "@/utils/broadcast/NotificationUtils.ts";
import NotificationPopupItem from "@/components/navBar/notifications/NotificationPopupItem.vue";
import GeneralAnnouncementsUtil from "@/utils/common/GeneralAnnouncementsUtil.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import MegaphoneIcon from "@/components/announcements/MegaphoneIcon.vue";
import AnnouncementDots from "@/components/announcements/AnnouncementDots.vue";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import DTOUserNotification = namaTypes.DTOUserNotification;

const props = defineProps<{ notifications: namaTypes.DTOTabularResult[] }>();
const emit = defineEmits<{ 'close-toast': [] }>();

const {notificationsCount} = storeToRefs(useLoginInfoStore());
const index = GeneralAnnouncementsUtil.index;
const currentAnnouncement = computed(() => GeneralAnnouncementsUtil.current());
const announcementsCount = computed(() => GeneralAnnouncementsUtil.announcements.value.length);
const unreadCount = computed(() => notificationsCount.value || props.notifications.length);

onUnmounted(() => useNotificationsStore().clearNotifications());

watch(() => props.notifications.length + announcementsCount.value, total => {
  if (total === 0)
    emit('close-toast');
});

const positionLabel = computed(() => `${index.value + 1}/${announcementsCount.value}`);

const viewAllNotificationsLink = computed(() => NotificationUtils.viewAllNotificationsLink());
</script>

<template>
  <div dir="ltr" class="tw-flex tw-flex-col tw-gap-3 tw-w-full tw-h-full tw-text-start">

    <div v-if="currentAnnouncement"
         class="tw-flex tw-flex-col tw-h-full tw-max-h-[210px] tw-overflow-hidden tw-rounded-[14px]
                tw-border tw-border-[#aab8d0] dark:tw-border-[#6d798b] tw-bg-gray-200 dark:tw-bg-gray-600">
      <div
        class="tw-h-[3px] tw-flex-none tw-bg-[image:linear-gradient(90deg,var(--nama-primary-light),color-mix(in_srgb,var(--nama-primary-light)_55%,white))] dark:tw-bg-[image:linear-gradient(90deg,var(--nama-primary-dark),color-mix(in_srgb,var(--nama-primary-dark)_60%,white))]"></div>
      <div class="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-gap-2 tw-px-[18px] tw-py-4 tw-h-52">
        <div class="tw-flex tw-items-center tw-gap-3">
          <div class="tw-flex tw-font-semibold tw-text-[color:var(--nama-primary-light)]
          dark:tw-text-[color:color-mix(in_srgb,var(--nama-primary-dark)_45%,white)] tw-bg-[color:color-mix(in_srgb,var(--nama-primary-light)_8%,white)]
           dark:tw-bg-[color:color-mix(in_srgb,var(--nama-primary-dark)_18%,transparent)] tw-gap-2 tw-px-1.5 tw-py-0.5 tw-rounded-[9px] tw-text-xs">
            <MegaphoneIcon :size="18" class="tw-fill-primary-light dark:tw-fill-primary-dark"/>
            <span class="tw-flex tw-items-center tw-justify-center tw-w-fit tw-h-fit"
                  v-text="Translator.translateId(BSCEntities.GeneralAnnouncement + 's')"/>
          </div>
        </div>
        <div class="tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-overflow-x-hidden [overflow-wrap:anywhere]
                    tw-text-base tw-font-bold tw-leading-[1.5] tw-text-[color:color-mix(in_srgb,var(--nama-primary-light)_55%,black)] dark:tw-text-[color:color-mix(in_srgb,var(--nama-primary-dark)_20%,white)]"
             :title="currentAnnouncement.text" data-scrollbar-reveal v-text="currentAnnouncement.text"/>
        <div v-if="announcementsCount > 1" class="tw-flex tw-items-center tw-gap-2 tw-mt-0.5">
          <AnnouncementDots/>
          <div class="tw-flex-1"></div>
          <div class="tw-text-xs tw-text-[#4b5563] dark:tw-text-[#d5dce6]" v-text="positionLabel"/>
          <div class="tw-size-6 tw-rounded-md tw-border tw-border-[#aab8d0] dark:tw-border-[#6d798b]
                      tw-text-base tw-cursor-pointer tw-flex tw-items-center tw-justify-center
                      tw-text-[#4b5563] dark:tw-text-[#d5dce6] hover:tw-bg-[#c2c8d2] dark:hover:tw-bg-[#5d6877]
                      hover:tw-text-[color:var(--nama-primary-light)] dark:hover:tw-text-[color:color-mix(in_srgb,var(--nama-primary-dark)_40%,white)]"
               :title="Translator.translateId('previous')"
               @click="GeneralAnnouncementsUtil.previous()">
            <NamaIcon default-icon="nm-next" class="!tw-size-3 tw-rotate-180 dark:!tw-fill-white !tw-fill-gray-500"/>
          </div>
          <div class="tw-size-6 tw-rounded-md tw-border tw-border-[#aab8d0] dark:tw-border-[#6d798b]
                      tw-text-base tw-cursor-pointer tw-flex tw-items-center tw-justify-center
                      tw-text-[#4b5563] dark:tw-text-[#d5dce6] hover:tw-bg-[#c2c8d2] dark:hover:tw-bg-[#5d6877]
                      hover:tw-text-[color:var(--nama-primary-light)] dark:hover:tw-text-[color:color-mix(in_srgb,var(--nama-primary-dark)_40%,white)]"
               :title="Translator.translateId('next')"
               @click="GeneralAnnouncementsUtil.next()">
            <NamaIcon default-icon="nm-next" class="!tw-size-3 dark:!tw-fill-white !tw-fill-gray-500"/>
          </div>
        </div>
      </div>
    </div>

    <div v-if="notifications.length"
         class="tw-flex-none tw-overflow-hidden tw-rounded-[14px] tw-border tw-border-[#b6bdc9] dark:tw-border-[#67727f]
                tw-bg-gray-200 dark:tw-bg-gray-600">
      <div class="tw-min-h-[46px] tw-px-[18px] tw-flex tw-items-center tw-gap-3
                  tw-border-b tw-border-[#bfc6d1] dark:tw-border-[#5f6a78]">
        <div class="tw-flex tw-gap-1 tw-text-[11px]  tw-px-[7px] tw-py-0.5 tw-rounded-[5px]
                    tw-text-[color:color-mix(in_srgb,var(--nama-warning-light)_80%,black)] dark:tw-text-[color:color-mix(in_srgb,var(--nama-warning-dark)_45%,white)] tw-bg-[color:color-mix(in_srgb,var(--nama-warning-light)_15%,white)] dark:tw-bg-[color:color-mix(in_srgb,var(--nama-warning-dark)_25%,transparent)]">
          <span class="tw-font-semibold" v-text="Translator.translateId('newNotifications')"/>
          <span class="tw-font-semibold" v-text="unreadCount"/>
        </div>
        <div class="tw-flex-1"/>
        <div
          class="tw-text-[12.5px] tw-cursor-pointer tw-text-[color:var(--nama-primary-light)] dark:tw-text-[color:color-mix(in_srgb,var(--nama-primary-dark)_40%,white)] hover:tw-underline"
          @click="NotificationUtils.markAsReadTop5Notifications()" v-text="Translator.translateId('markAsRead')"/>
        <div class="tw-size-[26px] tw-flex-none tw-rounded-[7px] tw-text-[15px] tw-cursor-pointer
                    tw-flex tw-items-center tw-justify-center tw-text-[#6b7280] dark:tw-text-[#d5dce6]
                    hover:tw-bg-[#c2c8d2] dark:hover:tw-bg-[#5d6877] hover:tw-text-[#1f2937] dark:hover:tw-text-[#ffffff]"
             data-close-btn :title="Translator.translateId('close')">&#10005;
        </div>
      </div>
      <div class="tw-max-h-[246px] tw-overflow-auto">
        <NotificationPopupItem v-for="(notification, order) in notifications" :key="order"
                               :notification="notification.values as DTOUserNotification"/>
      </div>
      <div class="tw-px-[18px] tw-py-3 tw-flex tw-items-center tw-gap-3 tw-bg-[rgba(255,255,255,.34)] dark:tw-bg-[rgba(16,16,16,.25)]
                  tw-border-t tw-border-[#bfc6d1] dark:tw-border-[#5f6a78]">
        <a class="tw-text-[13px] tw-font-semibold tw-cursor-pointer tw-no-underline
                  tw-text-[#16233b] dark:tw-text-[#f3f6fa] hover:tw-text-[color:color-mix(in_srgb,var(--nama-primary-light)_85%,black)] dark:hover:tw-text-[color:color-mix(in_srgb,var(--nama-primary-dark)_40%,white)]"
           data-close-btn :href="viewAllNotificationsLink" v-text="Translator.translateId('viewAllNotifications')"/>
      </div>
    </div>
  </div>
</template>
